/**
 * Synthetic SPY series for local mode (issue #82).
 *
 * A geometric path that realises the cap-weighted SPX return of the served
 * row set, so excess vs SPY is meaningful offline: names that beat the
 * synthetic index show a positive gap. Live mode reads `benchmark_daily_bar`.
 */
import type { PriceSeries, SeriesQuery, WindowKey } from '@meridian/contracts';

import { capWeightedReturn } from '../../analytics/cap-weighted-return';
import { BENCHMARK_PRICE, BENCHMARK_TICKER } from '../../constants/benchmark';
import { WN } from '../../constants/windows';
import { syntheticSessions } from '../../series/synthetic-sessions';

import type { DerivedSecurity } from '../../universe/derive-security';

function geometricPath(first: number, last: number, count: number): readonly number[] {
  if (count < 2) return [last];
  const ratio = last / first;
  const head = Array.from(
    { length: count - 1 },
    (_, i) => first * Math.pow(ratio, i / (count - 1)),
  );
  return [...head, last];
}

/**
 * Geometric closes realising the cap-weighted return of `index` members.
 * QQQ uses NDX (hop 9); SPY uses SPX. A missing or unusable return is a flat path.
 */
export function indexBenchmarkCloses(
  rows: readonly DerivedSecurity[],
  window: WindowKey,
  index: 'SPX' | 'NDX',
  last: number,
): readonly number[] {
  const members = rows.filter((row) => row.idx.includes(index));
  const ret = capWeightedReturn(members, window);
  const usable = ret !== null && Number.isFinite(ret) && 1 + ret > 0 ? ret : 0;
  return geometricPath(last / (1 + usable), last, WN[window]);
}

export function buildBenchmarkSeries(
  rows: readonly DerivedSecurity[],
  query: SeriesQuery,
): PriceSeries {
  const bars = indexBenchmarkCloses(rows, query.window, 'SPX', BENCHMARK_PRICE);
  return {
    ticker: BENCHMARK_TICKER,
    window: query.window,
    bars: [...bars],
    sessions: [...syntheticSessions(bars.length)],
  };
}
